import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Activity, Search, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  user_signup: 'User Signup',
  user_login: 'User Login',
  user_created: 'User Created',
  user_role_updated: 'Role Updated',
  credential_created: 'Credential Created',
  credential_accessed: 'Credential Accessed',
  credential_updated: 'Credential Updated',
  credential_deleted: 'Credential Deleted',
  credential_shared: 'Credential Shared',
  attachment_uploaded: 'Attachment Uploaded',
  attachment_downloaded: 'Attachment Downloaded',
  attachment_deleted: 'Attachment Deleted'
};

const getActionColor = (action) => {
  if (action.includes('deleted')) return 'bg-accent/10 text-accent';
  if (action.includes('created') || action.includes('uploaded')) return 'bg-success/10 text-success';
  if (action.includes('accessed') || action.includes('downloaded')) return 'bg-primary/10 text-primary';
  return 'bg-gray-100 text-text-muted';
};

export default function AuditLogs({ user }) {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchLogs();
    }
  }, [user, fetchLogs]);

  // Filter logs when dependencies change
  useEffect(() => {
    if (!searchQuery) {
      setFilteredLogs(logs);
      return;
    }

    const filtered = logs.filter(
      (log) =>
        log.user_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.resource_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ACTION_LABELS[log.action]?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredLogs(filtered);
  }, [logs, searchQuery]);

  const exportToExcel = () => {
    const exportData = logs.map(log => ({
      'Timestamp': new Date(log.timestamp).toLocaleString(),
      'Action': ACTION_LABELS[log.action] || log.action,
      'User Name': log.user_name,
      'User Email': log.user_email,
      'Resource Type': log.resource_type,
      'Resource': log.resource_title,
      'Details': JSON.stringify(log.details)
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Audit Logs');
    XLSX.writeFile(wb, `keebox-audit-logs-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Audit logs exported successfully');
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Shield className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <p className="text-text-primary font-manrope text-xl">Admin access required</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-primary font-manrope text-xl">Loading audit logs...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight">
            Audit Logs
          </h1>
          <p className="text-text-secondary mt-2">
            Track all activities across your organization
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={exportToExcel}
            className="btn-outline flex items-center gap-2"
            data-testid="btn-export-audit-logs"
          >
            <Download size={20} />
            Export to Excel
          </button>
          <div className="flex items-center gap-2 text-text-muted">
            <Activity size={20} />
            <span className="font-medium">{logs.length} events</span>
          </div>
        </div>
      </div>

      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={20} />
        <input
          type="text"
          placeholder="Search logs by user, action, or resource..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full bg-white border-2 border-gray-200 pl-10 pr-4 py-3 focus:border-primary focus:outline-none text-text-primary"
          data-testid="input-search-logs"
        />
      </div>

      <div className="bg-white border-2 border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Timestamp</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Action</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">User</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Resource</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50" data-testid={`audit-log-${log.id}`}>
                  <td className="px-6 py-4 text-sm text-text-secondary whitespace-nowrap">
                    {new Date(log.timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-block px-2 py-1 text-xs font-medium uppercase ${getActionColor(log.action)}`}>
                      {ACTION_LABELS[log.action] || log.action}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-text-primary font-medium text-sm">{log.user_name}</p>
                      <p className="text-text-muted text-xs">{log.user_email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-text-primary text-sm">{log.resource_title || 'N/A'}</p>
                      <p className="text-text-muted text-xs uppercase">{log.resource_type}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-text-secondary max-w-xs truncate">
                    {JSON.stringify(log.details).substring(0, 50)}...
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLogs.length === 0 && (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-text-muted mx-auto mb-3" />
            <p className="text-text-secondary">No audit logs found</p>
          </div>
        )}
      </div>
    </div>
  );
}